import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { getApiBaseUrl, attachRetryInterceptor } from "../services/httpClient";
import { refreshWhatsappConfig } from "../services/whatsappTemplateService";
import { expandLegacyOperatorPermissions } from "../utils/expandLegacyOperatorPermissions";
import {
  isAppOperatorDailySessionExpired,
  rememberLoginRoute,
  loginRouteForRole,
} from "../utils/appOperatorSession";
// jwt-decode is currently unused in dev bypass mode but kept for future auth enhancements
// import { jwtDecode } from 'jwt-decode';

// Simple JWT decode without verification (for checking token type)
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const AuthContext = createContext(null);

const API_BASE_URL = getApiBaseUrl();
const DEV_BYPASS_TOKEN = "DEV_BYPASS"; // Must match backend DEV_BYPASS_TOKEN

// Keep auth state persistent on same device/browser until explicit logout.
const getAuthStorage = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage;
};

const getLegacySessionStorage = () => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage;
};

const AUTH_STORAGE_KEYS = [
  "token",
  "admin_token",
  "user_token",
  "admin_user",
  "user_user",
  "user",
  "admin_devUser",
  "user_devUser",
  "devUser",
  "adminToken",
  "userToken",
  "access_token",
  "auth_token",
  "authToken",
  "jwt",
];

const getTabScopedAuthKey = (storage, key) => {
  if (!storage || typeof storage.length !== 'number') return null;
  for (let index = 0; index < storage.length; index += 1) {
    const storedKey = storage.key(index);
    if (typeof storedKey === 'string' && storedKey.startsWith(`${key}::__tab_`)) {
      return storedKey;
    }
  }
  return null;
};

const clearAuthStorage = (storage) => {
  if (!storage) return;
  AUTH_STORAGE_KEYS.forEach((key) => {
    storage.removeItem(key);
    const tabScopedKey = getTabScopedAuthKey(storage, key);
    if (tabScopedKey) {
      storage.removeItem(tabScopedKey);
    }
  });
};

const clearAllAuthStorage = () => {
  const storage = getAuthStorage();
  const legacyStorage = getLegacySessionStorage();
  clearAuthStorage(storage);
  clearAuthStorage(legacyStorage);
};

const getSharedStorageValue = (storage, key) => {
  if (!storage) return null;
  const value = storage.getItem(key);
  if (value !== null) return value;

  const legacyStorage = getLegacySessionStorage();
  const legacyValue = legacyStorage?.getItem(key);
  if (legacyValue !== null) {
    storage.setItem(key, legacyValue);
    return legacyValue;
  }

  const tabScopedKey = getTabScopedAuthKey(storage, key);
  if (tabScopedKey) {
    const tabScopedValue = storage.getItem(tabScopedKey);
    if (tabScopedValue !== null) {
      storage.setItem(key, tabScopedValue);
      return tabScopedValue;
    }
  }

  return null;
};

// Determine session type based on current route
const getSessionType = () => {
  if (typeof window === "undefined") return "admin"; // Default for SSR
  const path = window.location.pathname;

  // User routes use user session
  if (
    path.startsWith("/user/") ||
    path === "/user-login" ||
    path.startsWith("/citizen/")
  ) {
    return "user";
  }

  // Admin routes use admin session
  if (
    path === "/aalogin" ||
    path === "/locsydashboard" ||
    path.startsWith("/locsydashboard/") ||
    path.startsWith("/dashboard") ||
    path.startsWith("/users") ||
    path.startsWith("/wallet") ||
    path.startsWith("/bills") ||
    path.startsWith("/coupons") ||
    path.startsWith("/job-alerts") ||
    path.startsWith("/settings") ||
    path.startsWith("/shops") ||
    path.startsWith("/net-centers") ||
    path.startsWith("/online-works") ||
    path.startsWith("/professions") ||
    path.startsWith("/app-admin") ||
    path.startsWith("/backup") ||
    path.startsWith("/dashboard-cards") ||
    path.startsWith("/tables") ||
    path.startsWith("/oldsite") ||
    path.startsWith("/dashboard/students") ||
    path.startsWith("/ld2hd") ||
    path.startsWith("/profile") ||
    path === "/"
  ) {
    return "admin";
  }

  // Business dashboard routes use admin session (they're admin-like)
  if (path.startsWith("/franchisee/") || path.startsWith("/locsy/")) {
    return "admin";
  }

  // Default to admin for unknown routes
  return "admin";
};

// Get storage keys based on session type
const getStorageKeys = (sessionType) => {
  if (sessionType === "user") {
    return {
      token: "user_token",
      user: "user_user",
      devUser: "user_devUser",
    };
  }
  return {
    token: "admin_token",
    user: "admin_user",
    devUser: "admin_devUser",
  };
};

// Check if token is expired
const isTokenExpired = (token) => {
  if (!token || token === DEV_BYPASS_TOKEN) return false;
  try {
    const decoded = decodeJWT(token);
    if (!decoded) return false;
    if (isAppOperatorDailySessionExpired(decoded)) return true;
    if (!decoded.exp) return false;
    // exp is in seconds, Date.now() is in milliseconds
    return decoded.exp * 1000 < Date.now();
  } catch (e) {
    return false;
  }
};

const redirectToLoginForToken = (token) => {
  const decoded = decodeJWT(token);
  const loginPath = loginRouteForRole(decoded?.role || decoded?.type);
  rememberLoginRoute(decoded?.role || decoded?.type);
  const currentPath = window.location.pathname;
  const isLoginPage =
    currentPath.includes("/login") ||
    currentPath.includes("/aalogin") ||
    currentPath === "/user-login" ||
    currentPath === "/locsydashboard";
  if (!isLoginPage) {
    setTimeout(() => {
      window.location.href = loginPath;
    }, 1500);
  }
};

const isAuthFreeRequest = (url = "", baseURL = "") => {
  // Normalize both URL parts because axios may store the path in `config.url`
  // and the prefix in `config.baseURL`.
  const full = `${baseURL || ""}${url || ""}`.toLowerCase();
  return (
    /\/auth\/(login|magic-login|forgot-password(?:\/step1|\/verify-otp|\/step2)?|reset-password)\b/.test(
      full,
    ) ||
    /\/franchisee\/admin\/(login|register|google-auth|forgot-password(?:\/step1|\/verify-otp|\/step2)?|forgot-pin(?:\/step1|\/verify-otp|\/step2)?|register\/send-otp)\b/.test(
      full,
    ) ||
    /\/app-admin-operators\/(login|google-auth)\b/.test(full)
  );
};

// Track if we've already shown the expired token message to avoid spam
let hasShownExpiredTokenMessage = false;

const isExternalRequest = (url = "", baseURL = "") => {
  const full = `${baseURL || ""}${url || ""}`.toLowerCase();
  if (/^https?:\/\//i.test(full)) {
    return (
      !full.includes("localhost") &&
      !full.includes("127.0.0.1") &&
      !full.includes("loksy.in") &&
      !full.includes("knls.in") &&
      !full.includes("ondigitalocean.app")
    );
  }
  return false;
};

// Setup axios request interceptor to always include token from tab-scoped storage
axios.interceptors.request.use(
  (config) => {
    const reqBaseURL = config?.baseURL || "";
    const reqURL = config?.url || "";

    // Never attach Authorization header for external requests (like Cloudinary)
    if (isExternalRequest(reqURL, reqBaseURL)) {
      if (config.headers) {
        if (typeof config.headers.delete === "function") {
          config.headers.delete("Authorization");
          config.headers.delete("authorization");
        } else {
          delete config.headers.Authorization;
          delete config.headers.authorization;
        }
      }
      return config;
    }

    // Never attach Authorization header for login/reset endpoints.
    // This avoids 431 errors when stale/corrupt large tokens exist in storage.
    if (isAuthFreeRequest(reqURL, reqBaseURL)) {
      // Remove from per-request headers
      config.headers = config.headers || {};
      delete config.headers.Authorization;
      delete config.headers.authorization;

      // Remove from axios defaults as well (otherwise it can still be merged in)
      if (axios?.defaults?.headers?.common) {
        delete axios.defaults.headers.common.Authorization;
        delete axios.defaults.headers.common.authorization;
      }
      return config;
    }

    // Determine session type and get appropriate storage keys
    const sessionType = getSessionType();
    const storageKeys = getStorageKeys(sessionType);

    // Always get the latest token from tab-scoped storage using the correct key
    const storage = getAuthStorage();
    const token = storage?.getItem(storageKeys.token);

    // Check if token is expired before sending request
    if (token && isTokenExpired(token)) {
      // Token is expired - clear it
      console.warn("Token expired - clearing expired token");
      if (!hasShownExpiredTokenMessage) {
        hasShownExpiredTokenMessage = true;
        toast.error("Your session has expired. Please log in again.");
        redirectToLoginForToken(token);
      }
      storage?.removeItem(storageKeys.token);
      storage?.removeItem(storageKeys.user);
      storage?.removeItem(storageKeys.devUser);
      delete axios.defaults.headers.common["Authorization"];

      // Reject the request to prevent it from going out
      return Promise.reject(new Error("Token expired. Please log in again."));
    }

    // Guardrail: malformed/very large token can cause 431 (header too large)
    if (token && token.length > 1800) {
      console.warn("Skipping oversized token in Authorization header");
      return config;
    }

    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Auto-retry transient failures (network error / timeout / 5xx) on the global
// axios instance so raw axios.get(...) calls across pages recover from a cold
// backend without the user manually refreshing.
attachRetryInterceptor(axios);

// Setup axios response interceptor for handling 401 session expiry.
// 403 Forbidden must NOT clear the session (e.g. role lacks permission for one endpoint).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 403) {
      return Promise.reject(error);
    }

    if (status === 401) {
      // Determine session type and get appropriate storage keys
      const sessionType = getSessionType();
      const storageKeys = getStorageKeys(sessionType);
      const storage = getAuthStorage();

      const token = storage?.getItem(storageKeys.token);
      if (token && isTokenExpired(token)) {
        // Token is expired - clear it and redirect to login
        if (!hasShownExpiredTokenMessage) {
          hasShownExpiredTokenMessage = true;
          console.warn("Token expired - clearing session");
          toast.error("Your session has expired. Please log in again.");
          redirectToLoginForToken(token);
        }

        storage?.removeItem(storageKeys.token);
        storage?.removeItem(storageKeys.user);
        storage?.removeItem(storageKeys.devUser);
        delete axios.defaults.headers.common["Authorization"];
      } else {
        // Token might be invalid for other reasons
        console.warn("API request returned 401 Unauthorized");
      }
    } else if (status === 429 || status >= 500) {
      // Keep session for transient backend failures.
      console.warn(
        `Transient API error (${status}) - keeping current session active`,
      );
    }
    return Promise.reject(error);
  },
);

const clearFranchiseeDashboardStatsCache = (queryClient) => {
  if (!queryClient) return;
  queryClient.removeQueries(["franchisee-dashboard-stats"]);
  queryClient.removeQueries("franchisee-dashboard-stats");
};

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from shared storage on mount
  useEffect(() => {
    // Determine session type and get appropriate storage keys
    const sessionType = getSessionType();
    const storageKeys = getStorageKeys(sessionType);

    // Restore session from shared localStorage or fallback legacy/session values.
    const storage = getAuthStorage();

    // Try direct storage first
    let storedToken = storage?.getItem(storageKeys.token);
    let storedUser = storage?.getItem(storageKeys.user);
    let storedDevUser = storage?.getItem(storageKeys.devUser);

    // If not found, try legacy storage and tab-scoped keys
    if (!storedToken) {
      const legacyStorage = getLegacySessionStorage();
      const legacyToken = legacyStorage?.getItem(storageKeys.token);
      const legacyUser = legacyStorage?.getItem(storageKeys.user);
      const legacyDevUser = legacyStorage?.getItem(storageKeys.devUser);

      if (legacyToken) {
        storedToken = legacyToken;
        storedUser = legacyUser;
        storedDevUser = legacyDevUser;
        // Migrate to localStorage
        if (storedToken) storage?.setItem(storageKeys.token, storedToken);
        if (storedUser) storage?.setItem(storageKeys.user, storedUser);
        if (storedDevUser) storage?.setItem(storageKeys.devUser, storedDevUser);
      }
    }

    // If still not found, try tab-scoped keys
    if (!storedToken) {
      const tabScopedKey = getTabScopedAuthKey(storage, storageKeys.token);
      if (tabScopedKey) {
        const tabScopedValue = storage?.getItem(tabScopedKey);
        if (tabScopedValue) {
          storedToken = tabScopedValue;
          // Migrate to shared storage
          storage?.setItem(storageKeys.token, storedToken);
        }
      }
    }

    const effectiveStoredToken = storedToken;
    const effectiveStoredUser = storedUser;
    const effectiveStoredDevUser = storedDevUser;

    if (!effectiveStoredToken) {
      setLoading(false);
      return;
    }

    if (isTokenExpired(effectiveStoredToken)) {
      const expiredPayload = decodeJWT(effectiveStoredToken);
      rememberLoginRoute(expiredPayload?.role || expiredPayload?.type);
      storage?.removeItem(storageKeys.token);
      storage?.removeItem(storageKeys.user);
      storage?.removeItem(storageKeys.devUser);
      delete axios.defaults.headers.common["Authorization"];
      setLoading(false);
      return;
    }

    // If we're using dev bypass token, don't validate with backend.
    // This allows "Skip for now (Dev mode)" to work even when backend is not yet deployed.
    if (effectiveStoredToken === DEV_BYPASS_TOKEN) {
      try {
        const devUser =
          storage?.getItem(storageKeys.devUser) || effectiveStoredUser;
        if (devUser) {
          const parsedUser = JSON.parse(devUser);
          setUser(parsedUser);
        }
      } catch (err) {
        // Ignore
      }
      setToken(effectiveStoredToken);
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${effectiveStoredToken}`;
      setLoading(false);
      return;
    }

    // Restore user from storage first (for immediate UI update)
    // This ensures session is active immediately, even if validation is slow
    if (effectiveStoredUser) {
      try {
        const parsedUser = JSON.parse(effectiveStoredUser);
        setUser(parsedUser);
        setToken(effectiveStoredToken);
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${effectiveStoredToken}`;
      } catch (err) {
        console.warn("Failed to restore user from storage:", err);
      }
    } else {
      // Even if no user in storage, keep token active
      setToken(effectiveStoredToken);
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${effectiveStoredToken}`;
    }

    // Validate token in background (non-blocking)
    // Session remains active even if validation fails
    validateToken(effectiveStoredToken, storageKeys);
  }, []); // Only run on mount

  // Update session when route changes (to switch between admin and user sessions)
  useEffect(() => {
    const sessionType = getSessionType();
    const storageKeys = getStorageKeys(sessionType);
    const storage = getAuthStorage();

    // Try direct storage first
    let storedToken = storage?.getItem(storageKeys.token);
    let storedUser = storage?.getItem(storageKeys.user);

    // If not found, try legacy storage
    if (!storedToken) {
      const legacyStorage = getLegacySessionStorage();
      storedToken = legacyStorage?.getItem(storageKeys.token);
      storedUser = legacyStorage?.getItem(storageKeys.user);
      if (storedToken) {
        storage?.setItem(storageKeys.token, storedToken);
        if (storedUser) storage?.setItem(storageKeys.user, storedUser);
      }
    }

    // If still not found, try tab-scoped keys
    if (!storedToken) {
      const tabScopedKey = getTabScopedAuthKey(storage, storageKeys.token);
      if (tabScopedKey) {
        storedToken = storage?.getItem(tabScopedKey);
        if (storedToken) {
          storage?.setItem(storageKeys.token, storedToken);
        }
      }
    }

    // Always check for token for the current route's session type
    // This allows separate tabs to maintain separate sessions
    if (storedToken) {
      // We have a token for this route's session type - use it
      if (storedToken !== token) {
        setToken(storedToken);
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${storedToken}`;
      }

      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          // Only update user if it's different (to avoid unnecessary re-renders)
          if (JSON.stringify(parsedUser) !== JSON.stringify(user)) {
            setUser(parsedUser);
          }
        } catch (err) {
          console.warn("Failed to parse user from storage:", err);
        }
      }
    } else {
      // No token for this route's session type - clear state for this tab only
      // Do not clear storage here; only in explicit logout.
      if (token) {
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common["Authorization"];
      }
    }
  }, [location.pathname]); // Only depend on pathname, not token/user to avoid loops

  const validateToken = async (authToken, storageKeys) => {
    try {
      // First, try to decode the token to check if it's an operator token
      const decoded = decodeJWT(authToken);

      // If it's an app-operator token, refresh profile + permissions from backend
      if (decoded && decoded.role === "app_operator") {
        try {
          const operatorResponse = await axios.get(
            `${API_BASE_URL}/app-admin-operators/me`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            },
          );

          if (operatorResponse.data?.success && operatorResponse.data?.data) {
            const latestOperator = operatorResponse.data.data;
            setUser(latestOperator);
            setToken(authToken);
            const storage = getAuthStorage();
            storage?.setItem(storageKeys.user, JSON.stringify(latestOperator));
            axios.defaults.headers.common["Authorization"] =
              `Bearer ${authToken}`;
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn("Failed to refresh app operator profile:", err);
        }
      }

      // For regular users, use auth/verify endpoint
      const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.data.success) {
        setUser(response.data.user);
        setToken(authToken);
        const storage = getAuthStorage();
        storage?.setItem(storageKeys.user, JSON.stringify(response.data.user));
        axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
      } else {
        throw new Error("Invalid token");
      }
    } catch (error) {
      console.warn("Token validation failed (keeping session active):", error);
      // DON'T automatically logout - keep session active
      // Try to restore from storage to maintain session
      try {
        const storage = getAuthStorage();
        const storedUser = storage?.getItem(storageKeys.user);
        if (storedUser) {
          const user = JSON.parse(storedUser);
          setUser(user);
          setToken(authToken);
          axios.defaults.headers.common["Authorization"] =
            `Bearer ${authToken}`;
        }
      } catch (err) {
        // If we can't restore from storage, still keep the token
        // Session will remain active until user manually logs out
        console.warn(
          "Could not restore user from storage, but keeping session active",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (authToken, authUser, sessionType = null) => {
    if (!authToken || !authUser) {
      console.warn("completeLogin called without token or user");
      return;
    }

    let resolvedUser = authUser;
    if (
      authUser.role === "app_operator" &&
      Array.isArray(authUser.permissions)
    ) {
      resolvedUser = {
        ...authUser,
        permissions: expandLegacyOperatorPermissions(authUser.permissions),
      };
    }

    // Determine session type - use provided type or detect from user role
    const detectedSessionType =
      sessionType ||
      (authUser.role === "user" || authUser.role === "customer"
        ? "user"
        : "admin");
    const storageKeys = getStorageKeys(detectedSessionType);
    const storage = getAuthStorage();
    const legacyStorage = getLegacySessionStorage();

    setToken(authToken);
    setUser(resolvedUser);
    rememberLoginRoute(resolvedUser.role);
    storage?.setItem(storageKeys.token, authToken);
    storage?.setItem(storageKeys.user, JSON.stringify(resolvedUser));
    // Keep legacy session keys in sync for any code still reading sessionStorage.
    legacyStorage?.setItem(storageKeys.token, authToken);
    legacyStorage?.setItem(storageKeys.user, JSON.stringify(resolvedUser));
    axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    clearFranchiseeDashboardStatsCache(queryClient);
    if (
      ["franchisee_admin", "net_center_admin", "franchisee_operator"].includes(
        resolvedUser.role,
      )
    ) {
      refreshWhatsappConfig().catch(() => { });
    }
  };

  const login = async (phone, password, userId, sessionType = null) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        phone,
        password,
        userId,
      });

      if (response.data.success) {
        // AA login 2FA: password OK, OTP required before session is issued.
        if (response.data.requires_otp) {
          return {
            success: true,
            requires_otp: true,
            challenge_token: response.data.challenge_token,
            otp_sent_to: response.data.otp_sent_to || [],
            local_dev_otp: response.data.local_dev_otp || null,
            message: response.data.message,
          };
        }

        const { token, user } = response.data;
        // Determine session type from user role or provided type
        const detectedSessionType =
          sessionType ||
          (user.role === "user" || user.role === "customer" ? "user" : "admin");
        completeLogin(token, user, detectedSessionType);
        toast.success("Login successful!");
        return { success: true, user };
      } else {
        throw new Error(response.data.message || "Login failed");
      }
    } catch (error) {
      // Citizen Login also accepts an approved Vyapar administrator's existing
      // phone and password. Keep this fallback scoped to the user session so
      // the normal admin/Vyapar login flow and permissions are unchanged.
      if (sessionType === "user" && error.response?.status === 401) {
        try {
          const vyaparResponse = await axios.post(
            `${API_BASE_URL}/franchisee/admin/login`,
            { phone, password },
          );
          const vyapar = vyaparResponse.data;
          if (vyapar?.success && vyapar.token && vyapar.user) {
            completeLogin(vyapar.token, vyapar.user, "user");
            toast.success("Vyapar login successful — opening Loksy Citizen.");
            return { success: true, user: vyapar.user, citizenVyapar: true };
          }
        } catch (vyaparError) {
          // Both credential stores rejected the login; show the standard safe
          // error below without revealing which account type exists.
        }
      }
      const message =
        error.response?.data?.message || error.message || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyAdminLoginOtp = async (challengeToken, otp) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/admin-login/verify-otp`,
        {
          challenge_token: challengeToken,
          otp,
        },
      );

      if (response.data?.success && response.data?.token && response.data?.user) {
        completeLogin(response.data.token, response.data.user, "admin");
        toast.success("Login successful!");
        return { success: true, user: response.data.user };
      }

      throw new Error(response.data?.message || "OTP verification failed");
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "OTP verification failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendAdminLoginOtp = async (challengeToken) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/admin-login/resend-otp`,
        {
          challenge_token: challengeToken,
        },
      );

      if (response.data?.success && response.data?.challenge_token) {
        return {
          success: true,
          challenge_token: response.data.challenge_token,
          otp_sent_to: response.data.otp_sent_to || [],
          local_dev_otp: response.data.local_dev_otp || null,
          message: response.data.message,
        };
      }

      throw new Error(response.data?.message || "Failed to resend OTP");
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to resend OTP";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const devBypassLogin = () => {
    const devUser = {
      id: "dev_admin",
      phone: "+910000000000",
      email: "dev@locsy.com",
      full_name: "Development Admin",
      role: "app_admin",
      status: "active",
      permissions: {
        users: ["create", "read", "update", "delete"],
        wallet: ["create", "read", "update", "delete"],
        bills: ["create", "read", "update", "delete"],
        coupons: ["create", "read", "update", "delete"],
        job_alerts: ["create", "read", "update", "delete"],
        online_works: ["create", "read", "update", "delete"],
        settings: ["create", "read", "update", "delete"],
      },
    };

    // Dev bypass is always admin session
    const storageKeys = getStorageKeys("admin");
    const storage = getAuthStorage();
    const legacyStorage = getLegacySessionStorage();

    setUser(devUser);
    setToken(DEV_BYPASS_TOKEN);
    storage?.setItem(storageKeys.token, DEV_BYPASS_TOKEN);
    storage?.setItem(storageKeys.devUser, JSON.stringify(devUser));
    storage?.setItem(storageKeys.user, JSON.stringify(devUser));
    legacyStorage?.setItem(storageKeys.token, DEV_BYPASS_TOKEN);
    legacyStorage?.setItem(storageKeys.devUser, JSON.stringify(devUser));
    legacyStorage?.setItem(storageKeys.user, JSON.stringify(devUser));
    axios.defaults.headers.common["Authorization"] =
      `Bearer ${DEV_BYPASS_TOKEN}`;
    toast.info("Development mode: logged in without credentials");
    return { success: true, dev: true };
  };

  const logout = () => {
    // Check user role BEFORE clearing state
    const userRole = user?.role;

    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
    clearAllAuthStorage();
    clearFranchiseeDashboardStatsCache(queryClient);

    toast.info("Logged out successfully");

    // Return redirect path based on user role
    // Each user type should redirect to their respective login page
    if (userRole === "user" || userRole === "customer") {
      return "/user-login";
    }

    if (userRole === "app_operator") {
      return "/app-admin-operator/login";
    }

    if (
      userRole === "franchisee_admin" ||
      userRole === "net_center_admin" ||
      userRole === "franchisee_operator"
    ) {
      return "/locsydashboard";
    }

    // Default redirect to admin login for app_admin, super_admin, etc.
    return "/aalogin";
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const hasPermission = (module, action) => {
    if (!user || !user.permissions) return false;
    const modulePermissions = user.permissions[module] || [];
    return modulePermissions.includes(action);
  };

  /** Super-admin / app-admin: not gated. net_center_admin: all franchise features. Franchisee: must be in assigned list from login. */
  const hasFranchiseFeature = (featureKey) => {
    if (!user || !featureKey) return false;
    if (user.role === "super_admin" || user.role === "app_admin") return true;
    if (user.role === "net_center_admin") return true;
    if (user.role !== "franchisee_admin") return false;
    const features = Array.isArray(user.features) ? user.features : [];
    const enabled = (key) =>
      features.some(
        (f) =>
          (typeof f === "string" && f === key) ||
          (typeof f === "object" &&
            f.feature_name === key &&
            f.is_enabled),
      );
    if (enabled(featureKey)) return true;
    // Bill creation modules include View Bills — no separate enrollment needed.
    if (
      featureKey === "view_bills" &&
      (enabled("smartbill") || enabled("cart_model_bill"))
    ) {
      return true;
    }
    return false;
  };

  // Consider authenticated if:
  // 1. Dev bypass token exists, OR
  // 2. Token exists (even if user hasn't loaded yet - prevents logout on refresh)
  // This ensures session persists across page refreshes
  const isAuthenticated = token === DEV_BYPASS_TOKEN || !!token;

  // Get default route based on user role
  const getDefaultRoute = (userRole) => {
    switch (userRole) {
      case "app_admin":
      case "super_admin":
        return "/dashboard";
      case "app_operator":
        return "/dashboard";
      case "shop_admin":
      case "business_admin":
      case "business_owner":
      case "shop_owner":
        return "/locsy/dashboard";
      case "franchisee_admin":
      case "net_center_admin":
      case "franchisee_operator":
        return "/locsy/dashboard";
      case "user":
      case "customer":
        return "/user/coupons";
      default:
        return "/dashboard";
    }
  };

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated,
    login,
    verifyAdminLoginOtp,
    resendAdminLoginOtp,
    devBypassLogin,
    logout,
    hasRole,
    hasPermission,
    hasFranchiseFeature,
    setUser,
    getDefaultRoute,
    completeLogin,
  }), [user, token, loading, isAuthenticated, login, verifyAdminLoginOtp, resendAdminLoginOtp, devBypassLogin, logout, hasRole, hasPermission, hasFranchiseFeature, setUser, getDefaultRoute, completeLogin]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
